import { useMemo } from 'react';
import { TFunction } from 'i18next';

import { isLeader, loaders } from 'entities';
import { useRouteParams } from 'routes';

import { Column, Tabs } from 'components/Blocks';
import { GoBackButton } from 'components/Buttons';

import Loading from './Loading';
import TeamServices from './Services';
import { TeamProjectTags } from './ProjectTags';
import Users from './Users';
import TeamData from './TeamData';

import { teamProfileContext } from './context';
import { usePageTitle, useUrlStateV2, useUser } from 'hooks';
import { sortByProperty } from 'utils/array';

const teamComponents: Record<string, typeof TeamServices> = {
  DATA: TeamData,
  SERVICES: TeamServices,
  PROJECT_TAGS: TeamProjectTags,
  USERS: Users,
};

function TeamProfile() {
  const loggedUser = useUser();

  const { teamId } = useRouteParams('teamProfile');

  const { data, errorOrLoading, overwrite } = loaders.teams.find.use({
    params: { id: teamId },
    parser: (team) => ({
      ...team,

      // Ensures we can sort properly
      services: team.services.map((s) => ({
        ...s,
        disabled: s.disabled ?? false,
      })),

      projectTags: team.projectTags.map((s) => ({
        ...s,
        disabled: s.disabled ?? false,
      })),
    }),
  });

  usePageTitle(data?.name);

  const tabs = [
    {
      value: 'DATA',
      getLabel: (t: TFunction) => t('data'),
    },
    {
      value: 'SERVICES',
      getLabel: (t: TFunction) => t('services'),
      hide: !data?.hasServices,
    },
    {
      value: 'PROJECT_TAGS',
      getLabel: (t: TFunction) => t('tags'),
      hide: !data?.canAssumeProject,
    },
    {
      value: 'USERS',
      getLabel: (t: TFunction) => t('users'),
    },
  ];

  const [{ tab }, updateUrl] = useUrlStateV2({
    tab: {
      defaultValue: 'DATA',
      validate: (v) => !!tabs.filter((t) => !t.hide).find((t) => t.value === v),
    },
  });

  const ctxData = useMemo(
    () => ({
      updateTeam: overwrite,
      canMutateEntities:
        loggedUser.isSysAdmin || (isLeader(loggedUser) && teamId === loggedUser.team.id),

      team: {
        ...data!,

        services: sortByProperty(data?.services || [], ['disabled', 'name']),
      },
    }),
    [data, overwrite, loggedUser, teamId],
  );

  if (errorOrLoading) return <Loading />;

  const TabComponent = teamComponents[tab];

  return (
    <teamProfileContext.Provider value={ctxData}>
      <Column>
        <Column gap="1">
          <h4 className="flex items-center gap-2">
            <GoBackButton />

            {data?.name}
          </h4>

          <Tabs value={tab} onTabChange={(tab) => updateUrl({ tab })} tabs={tabs} />
        </Column>

        {TabComponent && <TabComponent />}
      </Column>
    </teamProfileContext.Provider>
  );
}

export default TeamProfile;
